function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { CssContainer } from "../../components/container";
import { PageHeader, Card } from "../../components/analytics-kit";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import { hasTenantFeature } from "../../functions/tenant-features";
import { usd, resolveBusinessModel } from "../sales/territory-common";
import { isAdminUser } from "../stores/common";
import { getDealerTargets, seedDealerTargets, putDealerTargets, targetRows } from "../sales/sales-api";

/*
 * Dealer Targets grid (SALES_FEATURES_REVIEW N1; the Store Targets grid
 * relabelled). Dealers × months of revenue goals. "Seed from last year + N%"
 * pre-fills every EMPTY cell from that dealer-month's actual × growth (the
 * server keeps saved cells). CSV paste feeds the same grid. Everything saves
 * through the one PUT /v1/sales/dealers/targets (rows {dealerId, period,
 * revenueGoal}). Owner/administrator gated: targets are the plan.
 */
class DealerTargetsView extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "setYear", year => {
      this.setState({
        year
      }, () => this.load(year));
    });
    _defineProperty(this, "seed", () => {
      const {
        year,
        growth
      } = this.state;
      this.setState({
        busy: true
      });
      seedDealerTargets(`${year}-01`, `${year}-12`, Number(growth) || 0).then(g => {
        if (!this._alive) return;
        if (g === null) {
          this.setState({
            busy: false,
            savedNote: "Seeding is not available yet for dealer targets. Enter goals directly or upload a CSV."
          });
          return;
        }
        // Seeded cells arrive suggested=true; saved cells come back as-is.
        // Mark every suggested cell dirty so Save writes the accepted plan.
        const dirty = {
          ...this.state.dirty
        };
        targetRows(g).forEach(row => {
          Object.entries(row.cells || {}).forEach(([period, c]) => {
            if (c && c.suggested && c.revenueGoal != null) dirty[this.cellKey(row.dealerId, period)] = String(c.revenueGoal);
          });
        });
        this.setState({
          grid: g || {},
          dirty,
          busy: false
        });
      }).catch(() => this._alive && this.setState({
        busy: false
      }));
    });
    _defineProperty(this, "save", () => {
      const {
        dirty
      } = this.state;
      const targets = Object.entries(dirty).filter(([, v]) => String(v).trim() !== "" && Number.isFinite(Number(v))).map(([k, v]) => {
        const [dealerId, period] = k.split("|");
        return {
          dealerId,
          period,
          revenueGoal: Number(v)
        };
      });
      if (!targets.length) return;
      this.setState({
        busy: true
      });
      putDealerTargets(targets).then(() => {
        if (!this._alive) return;
        this.setState({
          busy: false,
          savedNote: `Saved ${targets.length} monthly targets.`
        });
        this.load();
      }).catch(() => this._alive && this.setState({
        busy: false,
        savedNote: "Save failed. Try again."
      }));
    });
    _defineProperty(this, "applyCsv", () => {
      // dealerId,period,revenue_goal, one per line; header row skipped if present.
      const {
        grid,
        csvText
      } = this.state;
      const known = new Set(targetRows(grid).map(r => String(r.dealerId)));
      const dirty = {
        ...this.state.dirty
      };
      let applied = 0;
      let skipped = 0;
      csvText.split(/\r?\n/).forEach(line => {
        const parts = line.split(",").map(p => p.trim().replace(/^"|"$/g, ""));
        if (parts.length < 3) return;
        const [dealerId, period, goal] = parts;
        if (!/^\d{4}-\d{2}$/.test(period) || !Number.isFinite(Number(goal))) {
          if (dealerId) skipped++;
          return;
        }
        if (!known.has(dealerId)) {
          skipped++;
          return;
        }
        dirty[this.cellKey(dealerId, period)] = String(Number(goal));
        applied++;
      });
      this.setState({
        dirty,
        csvOpen: false,
        csvText: "",
        savedNote: `${applied} cells filled from CSV${skipped ? ` · ${skipped} rows skipped (unknown dealer or bad period)` : ""}. Review, then Save.`
      });
    });
    // Ready-to-edit template: one row per dealer × month, pre-filled with the
    // current goal, so the exact dealer ids and periods are right.
    _defineProperty(this, "downloadTemplate", () => {
      const {
        grid,
        year
      } = this.state;
      const rows = targetRows(grid);
      const periods = grid && grid.periods || [];
      let csv = "dealerId,period,revenue_goal\n";
      rows.forEach(r => periods.forEach(p => {
        csv += `${r.dealerId},${p},${Number(this.valueOf(r, p)) || 0}\n`;
      }));
      const blob = new Blob([csv], {
        type: "text/csv;charset=utf-8"
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dealer-targets-${year}-template.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    });
    _defineProperty(this, "onFile", e => {
      const file = e.target.files && e.target.files[0];
      e.target.value = ""; // allow re-picking the same file
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => this.setState({
        csvText: String(reader.result || "")
      }, this.applyCsv);
      reader.readAsText(file);
    });
    const y = new Date().getFullYear();
    this.state = {
      year: y,
      grid: undefined,
      error: false,
      growth: 8,
      dirty: {},
      busy: false,
      csvOpen: false,
      csvText: "",
      businessModel: null
    };
    this._alive = true;
  }
  componentDidMount() {
    resolveBusinessModel().then(bm => this._alive && this.setState({
      businessModel: bm
    })).catch(() => this._alive && this.setState({
      businessModel: "both"
    }));
    this.load();
  }
  componentWillUnmount() {
    this._alive = false;
  }
  load(year = this.state.year) {
    this.setState({
      grid: undefined,
      dirty: {},
      error: false
    });
    getDealerTargets(`${year}-01`, `${year}-12`).then(g => this._alive && this.setState({
      grid: g === null ? null : g || {}
    })).catch(() => this._alive && this.setState({
      error: true
    }));
  }
  cellKey(dealerId, period) {
    return `${dealerId}|${period}`;
  }
  valueOf(row, period) {
    const k = this.cellKey(row.dealerId, period);
    if (k in this.state.dirty) return this.state.dirty[k];
    const c = (row.cells || {})[period];
    return c && c.revenueGoal != null ? c.revenueGoal : "";
  }
  isSuggested(row, period) {
    const k = this.cellKey(row.dealerId, period);
    if (k in this.state.dirty) return false;
    const c = (row.cells || {})[period];
    return !!(c && c.suggested);
  }
  edit(row, period, v) {
    const clean = v.replace(/[^0-9.]/g, "");
    this.setState(s => ({
      dirty: {
        ...s.dirty,
        [this.cellKey(row.dealerId, period)]: clean
      }
    }));
  }
  render() {
    if (!hasTenantFeature("territoryRep") || this.state.businessModel === "retail") {
      return /*#__PURE__*/React.createElement(CssContainer, {
        cls: "margin-squeeze"
      }, /*#__PURE__*/React.createElement("p", {
        className: "module-empty"
      }, "Dealer performance is not configured for this workspace."));
    }
    if (!isAdminUser()) {
      return /*#__PURE__*/React.createElement(CssContainer, {
        cls: "margin-squeeze"
      }, /*#__PURE__*/React.createElement("p", {
        className: "module-empty"
      }, "Dealer targets are available to workspace owners and administrators."));
    }
    const {
      grid,
      error,
      year,
      growth,
      dirty,
      busy,
      csvOpen,
      csvText,
      savedNote
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, null);
    if (grid === undefined) return /*#__PURE__*/React.createElement(Loader, null);
    if (grid === null) {
      return /*#__PURE__*/React.createElement("div", {
        className: "territory-view stores-view wa-view"
      }, /*#__PURE__*/React.createElement(PageHeader, {
        crumbs: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Link, {
          to: "/Dealers/this_year"
        }, "Dealers"), " \xB7 Targets"),
        title: "Dealer targets",
        subtitle: "Monthly revenue goals per dealer"
      }), /*#__PURE__*/React.createElement(Card, {
        title: "Dealer targets"
      }, /*#__PURE__*/React.createElement("p", {
        className: "territory-hint",
        style: {
          margin: 0
        }
      }, "Dealer targets are not available yet for this workspace. They appear here once the dealer performance rail is enabled.")));
    }
    const periods = grid.periods || [];
    const rows = targetRows(grid);
    const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const dirtyCount = Object.keys(dirty).filter(k => String(dirty[k]).trim() !== "").length;
    const rowTotal = r => periods.reduce((a, p) => a + (Number(this.valueOf(r, p)) || 0), 0);
    const grandTotal = rows.reduce((a, r) => a + rowTotal(r), 0);
    const thisYear = new Date().getFullYear();
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view stores-view dealers-view wa-view"
    }, /*#__PURE__*/React.createElement(PageHeader, {
      crumbs: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Link, {
        to: "/Dealers/this_year"
      }, "Dealers"), " \xB7 Targets"),
      title: "Dealer targets",
      subtitle: `Monthly revenue goals per dealer · plan total ${usd(grandTotal)}${dirtyCount ? ` · ${dirtyCount} unsaved` : ""} · seed from last year plus a growth rate, then nudge any month and save`,
      chips: [{
        k: "Year",
        v: String(year)
      }],
      actions: /*#__PURE__*/React.createElement("span", {
        className: "seg"
      }, [thisYear - 1, thisYear, thisYear + 1].map(y => /*#__PURE__*/React.createElement("button", {
        key: y,
        className: year === y ? "on" : "",
        onClick: () => this.setYear(y)
      }, y)))
    }), /*#__PURE__*/React.createElement(Card, {
      title: `Monthly revenue goals · ${year}`,
      hint: "shaded green = seeded suggestion \xB7 shaded red = your unsaved edit"
    }, /*#__PURE__*/React.createElement("div", {
      className: "territory-toolbar",
      style: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
        marginBottom: 10
      }
    }, /*#__PURE__*/React.createElement("label", {
      style: {
        fontSize: 13
      }
    }, "Seed from ", year - 1, " +"), /*#__PURE__*/React.createElement("input", {
      type: "number",
      value: growth,
      min: -50,
      max: 100,
      step: 1,
      style: {
        width: 58,
        height: 36,
        margin: 0,
        boxSizing: "border-box",
        padding: "0 8px",
        textAlign: "center",
        fontSize: 13,
        border: "1px solid var(--plk-border, #dce0e5)",
        borderRadius: 8
      },
      onChange: e => this.setState({
        growth: e.target.value
      })
    }), /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 13,
        color: "var(--plk-muted, #777)"
      }
    }, "%"), /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
      disabled: busy,
      onClick: this.seed
    }, "Seed from last year"), /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
      disabled: busy,
      onClick: () => this.setState({
        csvOpen: !csvOpen
      })
    }, "Upload"), /*#__PURE__*/React.createElement("span", {
      style: {
        flex: 1
      }
    }), /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 13,
        color: "var(--plk-muted, #777)"
      }
    }, "Plan total ", usd(grandTotal), dirtyCount ? ` · ${dirtyCount} unsaved` : ""), /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
      disabled: busy || dirtyCount === 0,
      onClick: this.save
    }, "Save targets")), savedNote && /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        marginBottom: 8
      }
    }, savedNote), csvOpen && /*#__PURE__*/React.createElement("div", {
      style: {
        marginBottom: 10
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 8,
        flexWrap: "wrap"
      }
    }, /*#__PURE__*/React.createElement("label", {
      className: "k-button",
      style: {
        cursor: "pointer"
      }
    }, "Choose CSV file\u2026", /*#__PURE__*/React.createElement("input", {
      type: "file",
      accept: ".csv,text/csv",
      style: {
        display: "none"
      },
      onChange: this.onFile
    })), /*#__PURE__*/React.createElement("button", {
      className: "k-button",
      onClick: this.downloadTemplate
    }, "Download template"), /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 12,
        color: "var(--plk-muted, #777)"
      }
    }, "Columns: ", /*#__PURE__*/React.createElement("code", null, "dealerId, period (YYYY-MM), revenue_goal"), ". Or paste rows below.")), /*#__PURE__*/React.createElement("textarea", {
      rows: 5,
      style: {
        width: "100%",
        fontFamily: "monospace",
        fontSize: 12,
        padding: 8,
        boxSizing: "border-box"
      },
      placeholder: "dealerId,period,revenue_goal\n100234,2026-01,25000",
      value: csvText,
      onChange: e => this.setState({
        csvText: e.target.value
      })
    }), /*#__PURE__*/React.createElement("button", {
      className: "k-button",
      style: {
        marginTop: 6
      },
      onClick: this.applyCsv
    }, "Apply to grid")), /*#__PURE__*/React.createElement("div", {
      style: {
        overflowX: "auto"
      }
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table targets-grid"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Dealer"), periods.map(p => /*#__PURE__*/React.createElement("th", {
      key: p,
      className: "n"
    }, MONTHS[Number(p.slice(5)) - 1])), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Year"))), /*#__PURE__*/React.createElement("tbody", null, rows.map(r => /*#__PURE__*/React.createElement("tr", {
      key: r.dealerId
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", null, r.name || r.dealerId), r.assignedRepName || r.assignedRepId ? /*#__PURE__*/React.createElement("span", {
      style: {
        color: "var(--plk-muted,#777)",
        fontSize: 12
      }
    }, " \xB7 ", r.assignedRepName || r.assignedRepId) : null), periods.map(p => {
      const suggested = this.isSuggested(r, p);
      const k = this.cellKey(r.dealerId, p);
      return /*#__PURE__*/React.createElement("td", {
        key: p,
        className: "n",
        style: {
          padding: 2
        }
      }, /*#__PURE__*/React.createElement("input", {
        value: this.valueOf(r, p),
        onChange: e => this.edit(r, p, e.target.value),
        style: {
          width: 84,
          textAlign: "right",
          padding: "4px 6px",
          fontSize: 12.5,
          border: "1px solid var(--plk-border, #ddd)",
          borderRadius: 4,
          background: k in dirty ? "rgba(255,99,88,0.08)" : suggested ? "rgba(120,210,55,0.10)" : "transparent"
        }
      }));
    }), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, /*#__PURE__*/React.createElement("b", null, usd(rowTotal(r)))))))), !rows.length && /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "8px 0 0"
      }
    }, "No dealers on the roster yet. Dealers arrive from the wholesale sales feed.")), /*#__PURE__*/React.createElement("p", {
      className: "wa-foot"
    }, "Seed from last year plus N% pre-fills each dealer-month from its own ", year - 1, " actual, spread along that dealer's real seasonality. Nudge any number, then save.")));
  }
}
export default withRouter(DealerTargetsView);